import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, ArrowRight, Search, Filter, Tag as TagIcon, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import SEOHead from '../components/SEOHead';
import BlogCard from '../components/ui/blog-card';
import { BlogPostMetadata } from '../blog/types';
import { getAllPostsMetadata, getAllTags, searchPosts } from '../blog/registry';
import { useEmailCollection, EMAIL_COLLECTION_CONFIGS } from '../hooks/useEmailCollection';

function getSiteBaseUrl(): string {
  return import.meta.env.PROD
    ? 'https://memara.io'
    : (import.meta.env.VITE_APP_URL || 'http://localhost:3000').replace(/\/$/, '');
}

const BlogPage: React.FC = () => {
  const [allPosts, setAllPosts] = useState<BlogPostMetadata[]>([]);
  const [filteredPosts, setFilteredPosts] = useState<BlogPostMetadata[]>([]);
  const [availableTags, setAvailableTags] = useState<{ tag: string; count: number }[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Newsletter signup state and functionality
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const { submitEmail, isSubmitting, isSuccess, error: newsletterError } = useEmailCollection(EMAIL_COLLECTION_CONFIGS.NEWSLETTER);

  const handleNewsletterSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    const success = await submitEmail(newsletterEmail);
    if (success) {
      setNewsletterEmail(''); // Clear form on success
    }
  };

  useEffect(() => {
    const loadPosts = async () => {
      try {
        const [posts, tags] = await Promise.all([
          getAllPostsMetadata(),
          getAllTags()
        ]);
        setAllPosts(posts);
        setFilteredPosts(posts);
        setAvailableTags(tags);
      } catch (err) {
        console.error('Error loading blog posts:', err);
        setError('Failed to load blog posts');
      } finally {
        setLoading(false);
      }
    };

    loadPosts();
  }, []);

  // Filter posts based on search and tags
  useEffect(() => {
    const filterPosts = async () => {
      let filtered = allPosts;

      // Apply search filter
      if (searchQuery.trim()) {
        filtered = await searchPosts(searchQuery);
      }

      // Apply tag filters
      if (selectedTags.length > 0) {
        filtered = filtered.filter(post => 
          selectedTags.some(selectedTag => post.tags.includes(selectedTag))
        );
      }

      setFilteredPosts(filtered);
    };

    filterPosts();
  }, [searchQuery, selectedTags, allPosts]);

  const handleTagToggle = (tag: string) => {
    setSelectedTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const clearFilters = () => {
    setSelectedTags([]);
    setSearchQuery('');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
        <div className="container mx-auto px-4 py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading blog posts...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
        <div className="container mx-auto px-4 py-12">
          <div className="text-center">
            <p className="text-red-600">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  const baseUrl = getSiteBaseUrl();
  const blogIndexUrl = `${baseUrl}/blog`;
  const blogIndexDescription =
    'Memara blog: guides and product news on AI memory, semantic search, GPT and Claude integrations, n8n, Zapier, MCP, and building persistent context for agents.';

  const blogStructuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'Blog',
      name: 'Memara Blog',
      description: blogIndexDescription,
      url: blogIndexUrl,
      inLanguage: 'en-US',
      publisher: {
        '@type': 'Organization',
        name: 'Memara',
        url: baseUrl,
        logo: {
          '@type': 'ImageObject',
          url: `${baseUrl}/logos/memara-logo.svg`,
        },
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'Memara blog posts',
      numberOfItems: allPosts.length,
      itemListElement: allPosts.slice(0, 24).map((p, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: p.title,
        url: `${baseUrl}/blog/${p.slug}`,
      })),
    },
  ];

  return (
    <>
      <SEOHead
        title="Blog — AI memory, integrations & product updates"
        description={blogIndexDescription}
        keywords={[
          'Memara blog',
          'AI memory',
          'persistent memory',
          'semantic search',
          'GPT memory',
          'Claude memory',
          'MCP memory',
          'n8n memory',
          'AI agents',
          'vector memory',
        ]}
        canonicalUrl={blogIndexUrl}
        structuredData={blogStructuredData}
      />
      
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
        <div className="container mx-auto px-4 py-12">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-4xl md:text-5xl font-bold text-gray-900 mb-4"
            >
              Memory Development Insights
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-xl text-gray-600 max-w-2xl mx-auto mb-8"
            >
              Expert insights on building persistent memory systems for AI agents,
              GPTs, and intelligent workflows.
            </motion.p>

            {/* Search and Filter Controls */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="max-w-4xl mx-auto mb-8"
            >
              <div className="flex flex-col md:flex-row gap-4 items-center justify-center">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <Input
                    type="text"
                    placeholder="Search posts..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 bg-white border-gray-200 focus:border-indigo-500"
                  />
                </div>
                <Button
                  variant="outline"
                  onClick={clearFilters}
                  className="bg-white hover:bg-gray-50 border-gray-200"
                >
                  <Filter className="h-4 w-4 mr-2" />
                  Clear Filters
                </Button>
              </div>

              {/* Tag Filter Pills */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="flex flex-wrap gap-2 justify-center mt-4"
              >
                {availableTags.slice(0, 8).map(({ tag, count }) => (
                  <Badge
                    key={tag}
                    variant={selectedTags.includes(tag) ? "default" : "secondary"}
                    className={`cursor-pointer transition-all ${
                      selectedTags.includes(tag)
                        ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
                        : 'bg-white hover:bg-indigo-50 text-gray-700 border-gray-200'
                    }`}
                    onClick={() => handleTagToggle(tag)}
                  >
                    <TagIcon className="h-3 w-3 mr-1" />
                    {tag} ({count})
                  </Badge>
                ))}
              </motion.div>

              {/* Filter Status */}
              {(selectedTags.length > 0 || searchQuery) && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="mt-4 text-sm text-gray-600"
                >
                  Showing {filteredPosts.length} of {allPosts.length} posts
                  {selectedTags.length > 0 && (
                    <span> • Filtered by: {selectedTags.join(', ')}</span>
                  )}
                  {searchQuery && (
                    <span> • Search: "{searchQuery}"</span>
                  )}
                </motion.div>
              )}
            </motion.div>

            <div className="sr-only">
              Topics: vector database vs traditional database, semantic search vs full-text search, persistent AI memory, multi-tenant AI architecture, n8n AI memory integrations.
            </div>
          </motion.div>

          {/* Featured Post */}
          {filteredPosts.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="max-w-6xl mx-auto mb-16"
            >
              <BlogCard post={filteredPosts[0]} featured={true} />
            </motion.div>
          )}

          {/* Blog Posts Grid */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="max-w-7xl mx-auto"
          >
            {filteredPosts.length > 1 && (
              <div className="mb-12">
                <motion.h3
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.5 }}
                  className="text-2xl font-bold text-gray-900 mb-8"
                >
                  {searchQuery || selectedTags.length > 0 ? 'More Matching Posts' : 'More Posts'}
                </motion.h3>

                {/* Modern Grid Layout */}
                <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
                  {filteredPosts.slice(1, 7).map((post, index) => (
                    <BlogCard
                      key={post.id}
                      post={post}
                      index={index}
                      featured={false}
                    />
                  ))}
                </div>

                {/* Additional Posts in List Format */}
                {filteredPosts.length > 7 && (
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.6 }}
                    className="mt-12"
                  >
                    <h4 className="text-xl font-semibold text-gray-900 mb-6">All Posts</h4>
                    <div className="space-y-4">
                      {filteredPosts.slice(7).map((post, index) => (
                        <motion.article
                          key={post.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.4, delay: index * 0.1 }}
                          className="group bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-all duration-200 hover:border-indigo-200"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex flex-wrap gap-2 mb-2">
                                {post.tags.slice(0, 3).map((tag) => (
                                  <Badge
                                    key={tag}
                                    variant="secondary"
                                    className="text-xs bg-gray-100 text-gray-700"
                                  >
                                    {tag}
                                  </Badge>
                                ))}
                              </div>

                              <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-indigo-600 transition-colors">
                                <Link to={`/blog/${post.slug}`}>
                                  {post.title}
                                </Link>
                              </h3>

                              <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                                {post.excerpt}
                              </p>

                              <div className="flex items-center space-x-4 text-xs text-gray-500">
                                <span className="flex items-center">
                                  <Calendar className="w-3 h-3 mr-1" />
                                  {new Date(post.date).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric'
                                  })}
                                </span>
                                <span className="flex items-center">
                                  <Clock className="w-3 h-3 mr-1" />
                                  {post.readTime}
                                </span>
                                <span>by {post.author}</span>
                              </div>
                            </div>

                            <Button variant="ghost" size="sm" asChild className="ml-4">
                              <Link to={`/blog/${post.slug}`} className="flex items-center text-indigo-600 hover:text-indigo-700">
                                Read More
                                <ArrowRight className="w-4 h-4 ml-1" />
                              </Link>
                            </Button>
                          </div>
                        </motion.article>
                      ))}
                    </div>
                  </motion.div>
                )}
              </div>
            )}
            
            {/* No Posts Found State */}
            {filteredPosts.length === 0 && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                className="text-center py-12"
              >
                <div className="max-w-md mx-auto">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    className="text-gray-400 mb-4"
                  >
                    <Search className="h-16 w-16 mx-auto" />
                  </motion.div>
                  <motion.h3
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                    className="text-xl font-semibold text-gray-900 mb-2"
                  >
                    No posts found
                  </motion.h3>
                  <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                    className="text-gray-600 mb-6"
                  >
                    Try adjusting your search terms or clearing the filters.
                  </motion.p>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.5 }}
                  >
                    <Button onClick={clearFilters} variant="outline">
                      Clear All Filters
                    </Button>
                  </motion.div>
                </div>
              </motion.div>
            )}
          </motion.div>

          {/* Enhanced Call to Action */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.6 }}
            className="max-w-4xl mx-auto mt-20"
          >
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl overflow-hidden shadow-xl">
              <div className="bg-white bg-opacity-10 backdrop-blur-sm p-1">
                <div className="bg-white rounded-xl p-8 lg:p-12 text-center">
                  <motion.h3
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.7 }}
                    className="text-3xl font-bold text-gray-900 mb-4"
                  >
                    Ready to Build Persistent Memory?
                  </motion.h3>
                  <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.8 }}
                    className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto"
                  >
                    Start building AI agents with reliable memory in minutes, not days.
                    Join developers using Memara for production-ready memory systems.
                  </motion.p>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.9 }}
                    className="flex flex-col sm:flex-row gap-4 justify-center items-center"
                  >
                    <Button asChild size="lg" className="bg-indigo-600 hover:bg-indigo-700">
                      <Link to="/login">
                        Get Started Free
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </Link>
                    </Button>
                    <Button variant="outline" size="lg" asChild className="bg-white hover:bg-gray-50 border-gray-200">
                      <Link to="/blog/building-persistent-memory-ai-agents">
                        Read Getting Started Guide
                      </Link>
                    </Button>
                  </motion.div>

                  {/* Newsletter Signup */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: 1.0 }}
                    className="mt-8 pt-8 border-t border-gray-200"
                  >
                    <p className="text-sm text-gray-600 mb-4">
                      Get the latest memory development insights delivered to your inbox
                    </p>
                    {!isSuccess ? (
                      <form onSubmit={handleNewsletterSignup} className="flex flex-col sm:flex-row gap-2 max-w-md mx-auto">
                        <Input
                          type="email"
                          placeholder="Enter your email"
                          value={newsletterEmail}
                          onChange={(e) => setNewsletterEmail(e.target.value)}
                          disabled={isSubmitting}
                          required
                          className="bg-white border-gray-200"
                        />
                        <Button
                          type="submit"
                          disabled={isSubmitting}
                          variant="outline"
                          className="bg-white hover:bg-gray-50 border-gray-200 disabled:opacity-50"
                        >
                          {isSubmitting ? 'Subscribing...' : 'Subscribe'}
                        </Button>
                        {newsletterError && (
                          <p className="text-sm text-red-600 mt-2 text-center">{newsletterError}</p>
                        )}
                      </form>
                    ) : (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ duration: 0.3 }}
                        className="text-center py-4"
                      >
                        <CheckCircle className="w-6 h-6 text-green-500 mx-auto mb-2" />
                        <p className="text-green-700 font-medium">Successfully subscribed!</p>
                        <p className="text-sm text-gray-600">Thanks for subscribing to our newsletter.</p>
                      </motion.div>
                    )}
                  </motion.div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </>
      );
  };

export default BlogPage; 